import type { PageSeo } from "../data/page-seo";

export const SEO_OWNER_ATTRIBUTE = "data-zesrin-seo";
export interface SeoHeadElement {
  tag: "meta" | "link" | "script";
  attributes: Record<string, string>;
  text?: string;
}

/** Shared by initial HTML and client navigation; no browser or CMS dependency. */
export function seoHeadElements(seo: PageSeo): SeoHeadElement[] {
  const meta = (key: string, content: string): SeoHeadElement => ({
    tag: "meta", attributes: { [key.startsWith("og:") ? "property" : "name"]: key, content },
  });
  return [
    meta("keywords", seo.keywords.join(", ")),
    { tag: "link", attributes: { rel: "canonical", href: seo.canonical } },
    ...seo.alternates.map(({ language, href }): SeoHeadElement => ({ tag: "link", attributes: { rel: "alternate", hreflang: language, href } })),
    meta("og:title", seo.title), meta("og:description", seo.description),
    meta("og:url", seo.canonical), meta("og:type", "website"), meta("og:site_name", "Zesrin"),
    meta("og:locale", seo.locale === "zh" ? "zh_CN" : "en_US"),
    meta("twitter:card", "summary"),
    meta("twitter:title", seo.title), meta("twitter:description", seo.description),
    ...(seo.schema ? [{ tag: "script" as const, attributes: { type: "application/ld+json" }, text: JSON.stringify(seo.schema) }] : []),
  ];
}

export function escapeSeoHtml(value: string) {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!);
}

/** The template remains generic at build time so excluded routes keep their old HTML. */
export function renderSeoHtml(html: string, seo: PageSeo) {
  let result = html.replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<meta\b[^>]*\bname=["']description["'][^>]*>/gi, "");
  // Only remove tags owned by this feature, never a third party's verification tags.
  result = result.replace(/<(meta|link)\b[^>]*\bdata-zesrin-seo(?:=["'][^"']*["'])?[^>]*>/gi, "")
    .replace(/<script\b[^>]*\bdata-zesrin-seo(?:=["'][^"']*["'])?[^>]*>[\s\S]*?<\/script>/gi, "");
  result = result.replace(/<html\b([^>]*)>/i, (_match, attributes: string) =>
    `<html${attributes.replace(/\s+lang=["'][^"']*["']/i, "")} lang="${seo.locale === "zh" ? "zh-CN" : "en"}">`);
  const tags = seoHeadElements(seo).map(({ tag, attributes, text }) => {
    const attrs = Object.entries(attributes).map(([name, value]) => `${name}="${escapeSeoHtml(value)}"`).join(" ");
    return tag === "script"
      ? `<script ${SEO_OWNER_ATTRIBUTE}="true" ${attrs}>${text!.replace(/</g, "\\u003c")}</script>`
      : `<${tag} ${SEO_OWNER_ATTRIBUTE}="true" ${attrs}>`;
  });
  return result.replace(/<\/head>/i, () => `<title>${escapeSeoHtml(seo.title)}</title>\n<meta name="description" content="${escapeSeoHtml(seo.description)}">\n${tags.join("\n")}\n</head>`);
}
